/**
 * Компонент для анимации
 * появления элементов по скроллу
 * (при попадании во вьюпорт)
 */
import classNames from 'classnames';
import { ReactElement, cloneElement, forwardRef, useEffect, useRef, useState } from 'react';
import { useMounted } from '@/shared/hooks/use-mounted';
import { mergeRefs } from '@/shared/lib/utils/merge-refs';

type Props = {
    children: ReactElement;
    type?: string;
    /**
     * Задержка в миллисекундах (ms)
     */
    delay?: number;
    allowed?: boolean;
    instant?: boolean;
    useAnimationTimeline?: boolean;
    observerOptions?: IntersectionObserverInit;
};

const Reveal = forwardRef<Element, Props>(
    (
        { children, type, delay = 0, allowed = true, instant = false, useAnimationTimeline = false, observerOptions },
        ref,
    ) => {
        const el = useRef<HTMLDivElement>(null);
        const [revealed, setRevealed] = useState(false);
        const isMounted = useMounted();
        const supportsCSSViewTimeline =
            typeof window !== 'undefined' ? CSS.supports('animation-timeline', 'view()') : true;
        const animationTimelineEnabled = isMounted && useAnimationTimeline && supportsCSSViewTimeline;

        useEffect(() => {
            let observer: IntersectionObserver | null;
            let timeout: NodeJS.Timeout;

            if (instant) {
                if (allowed) {
                    timeout = setTimeout(() => {
                        setRevealed(true);
                    }, delay);
                }
            } else if (!animationTimelineEnabled) {
                observer = new IntersectionObserver((entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && allowed) {
                            obs.unobserve(entry.target);

                            timeout = setTimeout(() => {
                                setRevealed(true);
                            }, delay);
                        }
                    });
                }, observerOptions);

                if (el.current) {
                    observer.observe(el.current);
                }
            }

            return () => {
                clearTimeout(timeout);

                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
            };
        }, [delay, allowed, instant, animationTimelineEnabled, observerOptions]);

        return cloneElement(children, {
            ...children.props,
            ref: mergeRefs([el, ref]),
            'data-reveal': true,
            'data-reveal-type': type,
            className: classNames(children.props.className, {
                'is-revealed': revealed,
                'css-animation-timeline': animationTimelineEnabled,
            }),
        });
    },
);

Reveal.displayName = 'Reveal';

export default Reveal;
